import { useRef, useState, type DragEvent, type ReactNode } from 'react'

type Props = {
  onFiles: (files: File[]) => void
  // The native `accept` attribute for the hidden file input. Omit to allow any.
  accept?: string
  // When true, render without the outer card chrome so callers can place the
  // dropzone inside an existing card. Also reduces min-height.
  compact?: boolean
  // The body of the dropzone (typically a <p> with instructions). Wrapped
  // in the standard music-player__dropzone-text class by callers if desired.
  children: ReactNode
}

// Shared dropzone used by the Make routes. Wraps the
// dragover/dragleave/drop/click + hidden-input boilerplate so each route
// just provides a file handler and label content.
export default function Dropzone({ onFiles, accept, compact, children }: Props) {
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    if (!dragOver) setDragOver(true)
  }
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
    setDragOver(false)
  }
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(false)
    onFiles(Array.from(e.dataTransfer.files))
  }
  const handleClick = () => {
    inputRef.current?.click()
  }

  return (
    <div
      className={[
        'music-player__dropzone',
        compact ? 'music-player__dropzone--compact' : 'card music-player__panel',
        dragOver ? 'music-player__dropzone--active' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
      onDragOver={handleDragOver}
      onDragEnter={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple
        className="music-player__dropzone-input"
        onChange={(e) => {
          const files = e.target.files ? Array.from(e.target.files) : []
          onFiles(files)
          e.target.value = ''
        }}
      />
      {children}
    </div>
  )
}
